import jwtDecode from 'jwt-decode'
import SELF from '~/graphql/queries/self.gql'

const getDefaultState = () => ({
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  states: {},
  autoStates: {},
  isAuthenticated: false,
  isUsingSSO: false,
  isOpenly: false,
  avatarUrl: '', // Comes from Auth0
  permissions: {},
  roles: {},
  capabilities: {},
  timeOfLastRequest: null,
  agency: {},
})

export const state = () => getDefaultState()

export const mutations = {
  setSelf(state, self) {
    const {
      $bugsnag,
      $chat,
      $datadog,
      $segment,
    } = useNuxtApp()

    const wasNotAuthenticated = !state.isAuthenticated

    if (wasNotAuthenticated) {
      $bugsnag.setUser(self.user.id)

      $bugsnag.addMetadata('user', {
        name: `${self.user.firstName} ${self.user.lastName}`,
        email: self.user.email,
        roles: self.user.roles,
      })

      $datadog.setUser({
        id: self.user.id,
        name: `${self.user.firstName} ${self.user.lastName}`,
        email: self.user.email,
      })

      $chat.setupChat(self.zendeskChatJWT)
      $segment.initBaseEvents(self.user, self.permissions)
      state.isAuthenticated = true
    }

    state.id = self.user.id
    state.firstName = self.user.firstName
    state.lastName = self.user.lastName
    state.email = self.user.email
    state.agency = self.user.agency
    state.isUsingSSO = self.user.isOpenly // add -> || self.user.isUserSSO(or whatever the flag is called) when BE updates
    state.isOpenly = self.user.isOpenly

    let permissions
    if (Array.isArray(self.permissions)) {
      permissions = {}
      self.permissions.forEach(permissionID => (permissions[permissionID] = true))
    } else {
      permissions = self.permissions
    }
    state.permissions = permissions

    let roles
    if (Array.isArray(self.user.roles)) {
      roles = {}
      self.user.roles.forEach(roleID => (roles[roleID] = true))
    } else {
      roles = self.user.roles
    }
    state.roles = roles

    state.capabilities = self.capabilities ?? {}

    let states
    if (Array.isArray(self.user.states)) {
      states = {}
      self.user.states.forEach(stateID => (states[stateID] = true))
    } else {
      states = self.user.states
    }
    state.states = states

    let autoStates
    if (Array.isArray(self.user.autoStates)) {
      autoStates = {}
      self.user.autoStates.forEach(stateID => (autoStates[stateID] = true))
    } else {
      autoStates = self.user.autoStates
    }
    state.autoStates = autoStates ?? {}
  },
  setAvatarURL(state, avatarUrl) {
    state.avatarUrl = avatarUrl
  },
  setTimeOfLastRequest(state) {
    state.timeOfLastRequest = new Date()
  },
  clearSelf(state) {
    Object.assign(state, getDefaultState())
  },
}

export const actions = {
  async requestSelf({ commit, dispatch }, { forceUpdate } = {}) {
    const { $featureFlags, $urql } = useNuxtApp()

    // If `forceUpdate` is not `true`,
    // and the `self` state has an `id`,
    // and the `self` state has a `timeOfLastRequest`,
    // and it has been less than 15 minutes since the `timeOfLastRequest` sync the store with URQL's cache and return instead of refetching the SELF query.
    if (
      !forceUpdate && this.state.self.id &&
      this.state.self.timeOfLastRequest && (new Date() - this.state.self.timeOfLastRequest) / 1000 / 60 < 15
    ) {
      const { data } = await $urql.client.query(SELF, null, {
        requestPolicy: 'cache-only',
      })

      if (data) {
        commit('setSelf', data.self)
        return
      }
    }

    const { data, error } = await $urql.client.query(SELF, null, {
      requestPolicy: 'network-only',
    })

    if (error) {
      if (!this.state.self.id) {
        throw new Error('Could not acquire signed in user\'s information on application start.')
      }

      return
    }

    commit('setTimeOfLastRequest')
    try {
      await $featureFlags.setUser({
        ...data.self.user,
        permissions: data.self.permissions,
      })
    } finally {
      commit('setSelf', data.self)
      dispatch('getAvatarURL')
    }
  },
  getAvatarURL({ commit }) {
    const { public: config } = useRuntimeConfig()
    const token = useCookie(config.authTokenName)

    const decodedToken = jwtDecode(token.value)
    const avatarUrl = decodedToken['https://openly.com/avatar']

    if (this.state.self.avatarUrl === avatarUrl) {
      return
    }

    commit('setAvatarURL', avatarUrl)
  },
  async logout({ commit }) {
    const { $auth, $bugsnag, $featureFlags, $urql } = useNuxtApp()

    try {
      $urql.logout()
      await $featureFlags.closeClient()
    } catch (error) {
      $bugsnag.notify(error)
    } finally {
      commit('clearSelf')

      $auth.logout({
        returnTo: `${window.location.origin}/unauthenticate`,
      })
    }
  },
}
